import { useState, useEffect } from 'react';
import { 
    Modal, 
    Form, 
    Input, 
    Select, 
    Switch, 
    Button, 
    message, 
    Typography, 
    Divider,
    Space,
    Table,
    Tooltip,
    Tag, Flex,
    ColorPicker,
    Row,
    Col,
    Upload,
    Checkbox,
} from 'antd';
import {
    ShareAltOutlined,
    CopyOutlined,
    DeleteOutlined,
    EyeOutlined,
    BgColorsOutlined,
    UploadOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { generateShareLink, getFormShares, revokeShareLink, sharePublicLink } from '../../services/publicFormServices';
import { FORM_LANGUAGE_OPTIONS, DEFAULT_LANGUAGE } from '../../utils/formTranslations';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;
const { Option } = Select;

// Recipients of a share link have no app session to carry their language, so the
// language picked when the link was created rides along on the URL.
const withLanguage = (url, language) => {
    if (!url || !language || language === DEFAULT_LANGUAGE) return url;
    return `${url}${url.includes('?') ? '&' : '?'}lang=${encodeURIComponent(language)}`;
};

const THEME_PRESETS = [
    {
        label: 'Colors',
        colors: [
            '#141bcb', '#0a99fe', '#7c3aed', '#059669',
            '#dc2626', '#d97706', '#0891b2', '#1f2937',
            '#db2777', '#16a34a',
        ],
    },
];

const ShareFormModal = ({
    visible,
    onCancel,
    formId,
    formName,
    spaceId,
}) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const nonValidated = Form.useWatch('nonValidated', form);
    const [loading, setLoading] = useState(false);
    const [shares, setShares] = useState([]);
    const [generatingShare, setGeneratingShare] = useState(false);
    const [showRecipientsModal, setShowRecipientsModal] = useState(false)
    const [recipients, setRecipients] = useState("")
    const [latestUrl, setLatestUrl] = useState("")
    const [errorMessage, setErrorMessage] = useState("") 

    // Theme state
    const [themeColor, setThemeColor] = useState('#141bcb');
    const [showCompanyLogo, setShowCompanyLogo] = useState(false);
    const [headerText, setHeaderText] = useState('');
    const [customLogo, setCustomLogo] = useState(null);

    // Company logo from Redux (set in Administrator Controls)
    const { companyLogo } = useSelector(state => state.company);

    useEffect(() => {
        if (nonValidated) {
            form.setFieldsValue({ expiryDays: undefined });
        }
    }, [nonValidated, form]);

    // Fetch existing shares when modal opens
    useEffect(() => {
        const fetchExistingShares = async () => {
            try {
                setLoading(true);
                
                const response = await getFormShares(formId);
                
                if (response.status === 200) {
                    setShares(response.data.shares || []);
                }
            } catch (error) {
            } finally {
                setLoading(false);
            }
        };

        if (visible && formId) {
            fetchExistingShares();
        }
    }, [visible, formId]);

    const refetchShares = async () => {
        try {
            setLoading(true);
            
            const response = await getFormShares(formId);
            
            if (response.status === 200) {
                setShares(response.data.shares || []);
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateShareLink = async (values) => {
        try {
            setGeneratingShare(true);

            const logoUrl = customLogo || companyLogo;
            const submitValues = {
                ...values,
                spaceId: spaceId,
                nonValidated: values.nonValidated || false,
                theme: {
                    headerColor: themeColor,
                    showCompanyLogo: showCompanyLogo,
                    companyLogoUrl: showCompanyLogo ? logoUrl : null,
                    headerText: headerText,
                },
            };
            
            const response = await generateShareLink(formId, submitValues);

            if (response.status === 200) {
                message.success(t('Share link generated successfully!'));

                const publicUrl = withLanguage(response.data?.publicUrl, values.language);
                // Copy to clipboard
                await navigator.clipboard.writeText(publicUrl);
                setLatestUrl(publicUrl)
                message.info(t('Link copied to clipboard!'));
                setShowRecipientsModal(true)
                // Refresh shares list
                refetchShares();
                
                // Reset form and theme
                form.resetFields();
                setThemeColor('#141bcb');
                setShowCompanyLogo(false);
                setHeaderText('');
                setCustomLogo(null);
            } else {
                throw new Error(response.message || 'Failed to generate share link');
            }
        } catch (error) {
            message.error(t('Failed to generate share link'));
        } finally {
            setGeneratingShare(false);
        }
    };

    const copyToClipboard = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            message.success(t('Link copied to clipboard!'));
        } catch (error) {
            message.error(t('Failed to copy link'));
        }
    };

    const handleRevokeShare = async (shareToken) => {
        try {
            const response = await revokeShareLink(shareToken);

            if (response.status === 200) {
                message.success(t('Share link revoked successfully'));
                refetchShares();
            } else {
                throw new Error(response.message || 'Failed to revoke share');
            }
        } catch (error) {
            message.error(t('Failed to revoke share link'));
        }
    };

    const handleSubmit = (values) => {
        handleGenerateShareLink(values);
    };

    const getShareUrl = (record) => {
        return withLanguage(`${window.location.origin}/public/form/${record.shareToken}`, record.language);
    };

    const columns = [
        {
            title: t('Description'),
            dataIndex: 'description',
            key: 'description',
            width: 120,
            render: (text) => text || 'Untitled Share',
        },
        {
            title: t('Status'),
            dataIndex: 'isActive',
            key: 'status',
            width: 80,
            render: (isActive, record) => {
                const isExpired = moment().isAfter(moment(record.expiresAt));
                return (
                    <Tag color={isActive && !isExpired ? 'green' : 'red'}>
                        {isActive && !isExpired ? t('Active') : t('Expired')}
                    </Tag>
                );
            },
        },
        {
            title: t('Created'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 100,
            render: (date) => moment(date).format('MMM DD, YYYY'),
        },
        {
            title: t('Expires'),
            dataIndex: 'expiresAt',
            key: 'expiresAt',
            width: 100,
            render: (date) => moment(date).format('MMM DD, YYYY'),
        },
        {
            title: t('Last Access'),
            dataIndex: 'lastAccessedAt',
            key: 'lastAccessedAt',
            width: 110,
            render: (date) => date ? moment(date).format('MMM DD, YYYY') : t('Never'),
        },
        {
            title: t('Submissions'),
            dataIndex: 'submissionCount',
            key: 'submissionCount',
            width: 60,
            render: (count) => count || 0,
        },
        {
            title: t('Actions'),
            key: 'actions',
            width: 150,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small" wrap>
                    <Tooltip title={t('Copy Link')}>
                        <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => copyToClipboard(getShareUrl(record))}
                        />
                    </Tooltip>
                    <Tooltip title={t('View Submissions')}>
                        <Button
                            type="text"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => {
                                // TODO: Navigate to submissions view
                                message.info(t('Submissions view coming soon'));
                            }}
                        />
                    </Tooltip>
                    <Tooltip title={t('Revoke Share')}>
                        <Button
                            type="text"
                            size="small"
                            icon={<DeleteOutlined />}
                            danger
                            onClick={() => handleRevokeShare(record.shareToken)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    // A link counts as active only while it is enabled and not yet expired —
    // same rule as the Status column. Only offer generating a new link when none
    // are active.
    const hasActiveShare = shares.some(
        (s) => s.isActive && !moment().isAfter(moment(s.expiresAt))
    );

    const handleCloseRecipientModal = () => {
        setShowRecipientsModal(false)
    }

    const handleShareLink = async () => {
        if(recipients.length === 0){
            setErrorMessage(t("Please add recipients"));
            return;
        }
        let emails = recipients.split(", ");
        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        let invalidEmails = emails.filter(email => {
            return !emailPattern.test(email)
        })
        if(invalidEmails?.length > 0) {
            setErrorMessage(t("Invalid emails: ") + invalidEmails.join(", "))
            return;
        }
        setErrorMessage("")
        let postData = {link: latestUrl, toEmails: emails, formId: formId}
        let sharedResponse = await sharePublicLink(postData)
        if(sharedResponse?.status === 200) {
            message.success(sharedResponse.message)
            setShowRecipientsModal(false)
            setLatestUrl("")
            setRecipients("")
            setErrorMessage("")
        }
        
    }

    return (
        <Modal
            title={
                <Space>
                    <ShareAltOutlined />
                    <span>{t('Share Form')}: {formName}</span>
                </Space>
            }
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={1000}
            className='form-editor-preview'
            destroyOnHidden
        >
            {/* Generate New Share Section — shown only when no active links exist */}
            {!hasActiveShare && (
              <>
            <div style={{ marginBottom: 24 }}>
                <Title level={4}>{t('Generate New Share Link')}</Title>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        expiryDays: 30,
                        allowMultipleSubmissions: true,
                        nonValidated: false,
                        language: DEFAULT_LANGUAGE,
                    }}
                >
                    <Form.Item
                        name="description"
                        label={t('Description (Optional)')}
                    >
                        <Input placeholder={t('e.g., External Partner Survey')} />
                    </Form.Item>

                    <Form.Item
                        name="language"
                        label={t('Form Language')}
                        extra={t('Recipients open the form in this language. Fields without a translation fall back to English.')}
                    >
                        <Select
                            options={FORM_LANGUAGE_OPTIONS}
                        />
                    </Form.Item>

                    {/* <Form.Item
                        name="nonValidated"
                        valuePropName="checked"
                    >
                        <Checkbox>{t('Non-Validated')}</Checkbox>
                    </Form.Item> */}

                    <Form.Item
                        name="expiryDays"
                        label={t('Link Expires In')}
                        rules={nonValidated ? [] : [{ required: true, message: t('Please select expiry period') }]}
                    >
                        <Select allowClear={nonValidated} placeholder={nonValidated ? t('Optional') : undefined}>
                            <Option value={7}>7 {t('Days')}</Option>
                            <Option value={30}>30 {t('Days')}</Option>
                            <Option value={90}>90 {t('Days')}</Option>
                            <Option value={365}>1 {t('Year')}</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="allowMultipleSubmissions"
                        label={t('Allow Multiple Submissions')}
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>

                    {/* ── Theme Customization ── */}
                    <div
                        style={{
                            border: '1px solid #e8e8e8',
                            borderRadius: 8,
                            padding: '14px 16px',
                            marginBottom: 16,
                            background: '#fafafa',
                        }}
                    >
                        <div
                            style={{
                                fontWeight: 500,
                                marginBottom: 14,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                fontSize: 13,
                                color: '#374151',
                            }}
                        >
                            <BgColorsOutlined />
                            {t('Form Theme')}
                        </div>

                        <Row gutter={24} align="top">
                            {/* Left: live preview */}
                            <Col xs={24} sm={10}>
                                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
                                    {t('Preview')}
                                </div>
                                <div
                                    style={{
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 6,
                                        overflow: 'hidden',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                                    }}
                                >
                                    {/* Preview header */}
                                    <div
                                        style={{
                                            background: themeColor,
                                            padding: '8px 12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 6,
                                            minHeight: 36,
                                        }}
                                    >
                                        {showCompanyLogo && (customLogo || companyLogo) && (
                                            <img
                                                src={customLogo || companyLogo}
                                                alt={t('Company Logo')}
                                                style={{ height: 18, maxWidth: 70, objectFit: 'contain' }}
                                            />
                                        )}
                                        {headerText ? (
                                            <span style={{ color: 'white', fontSize: 12, fontWeight: 600 }}>
                                                {headerText}
                                            </span>
                                        ) : (
                                            (!showCompanyLogo || (!customLogo && !companyLogo)) ? (
                                                <>
                                                    <img
                                                        src="/flxflow_logo.svg"
                                                        alt={t('FlxFlow')}
                                                        style={{ height: 16 }}
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                    <span style={{ color: 'white', fontSize: 11, fontWeight: 600 }}>
                                                        {t('FlxFlow')}
                                                    </span>
                                                </>
                                            ) : null
                                        )}
                                    </div>
                                    {/* Preview body skeleton */}
                                    <div style={{ padding: '10px 12px', background: '#fff' }}>
                                        <div style={{ height: 7, background: '#1a56db22', borderRadius: 3, marginBottom: 6, width: '50%' }} />
                                        <div style={{ height: 5, background: '#e8e8e8', borderRadius: 3, marginBottom: 4 }} />
                                        <div style={{ height: 5, background: '#e8e8e8', borderRadius: 3, width: '78%', marginBottom: 4 }} />
                                        <div style={{ height: 5, background: '#e8e8e8', borderRadius: 3, width: '60%' }} />
                                    </div>
                                    {/* Preview footer */}
                                    <div
                                        style={{
                                            padding: '5px 12px',
                                            background: '#f9fafb',
                                            fontSize: 10,
                                            color: '#9ca3af',
                                            textAlign: 'center',
                                            borderTop: '1px solid #f0f0f0',
                                        }}
                                    >
                                        {t('Powered by')} <span style={{ fontWeight: 600 }}>{t('FlxFlow')}</span>
                                    </div>
                                </div>
                            </Col>

                            {/* Right: controls */}
                            <Col xs={24} sm={14}>
                                <div style={{ marginBottom: 14 }}>
                                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
                                        {t('Header Background Color')}
                                    </div>
                                    <ColorPicker
                                        value={themeColor}
                                        onChange={(color) => setThemeColor(color.toHexString())}
                                        presets={THEME_PRESETS}
                                        showText
                                        size="middle"
                                    />
                                </div>

                                <div style={{ marginBottom: 14 }}>
                                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
                                        {t('Show Company Logo in Header')}
                                    </div>
                                    <Space align="center">
                                        <Switch
                                            checked={showCompanyLogo}
                                            onChange={setShowCompanyLogo}
                                            size="small"
                                        />
                                        {showCompanyLogo && (customLogo || companyLogo) && (
                                            <img
                                                src={customLogo || companyLogo}
                                                alt={t('Company Logo')}
                                                style={{ height: 20, maxWidth: 60, objectFit: 'contain', borderRadius: 3 }}
                                            />
                                        )}
                                    </Space>
                                    {showCompanyLogo && (
                                        <div style={{ marginTop: 8 }}>
                                            <Upload
                                                accept="image/*"
                                                showUploadList={false}
                                                beforeUpload={(file) => {
                                                    const reader = new FileReader();
                                                    reader.onload = (e) => setCustomLogo(e.target.result);
                                                    reader.readAsDataURL(file);
                                                    return false;
                                                }}
                                            >
                                                <Button size="small" icon={<UploadOutlined />}>
                                                    {t('Upload Logo')}
                                                </Button>
                                            </Upload>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
                                        {t('Header Text')}
                                    </div>
                                    <Input
                                        placeholder={t('eg. Company Survey')}
                                        value={headerText}
                                        onChange={(e) => setHeaderText(e.target.value)}
                                        size="small"
                                    />
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            icon={<ShareAltOutlined />}
                            loading={generatingShare}
                            style={{ width: '100%' }}
                        >
                            {t('Generate Link and Share')}
                        </Button>
                    </Form.Item>
                </Form>
            </div>

            <Divider />
              </>
            )}

            {/* Existing Shares Section */}
            <div>
                <Title level={4}>{t('Active Share Links')}</Title>
                <Table
                    columns={columns}
                    dataSource={shares}
                    rowKey={(record) => record.shareToken || record._id?.timestamp}
                    loading={loading}
                    pagination={false}
                    size="small"
                    scroll={{ x: 700 }}
                    locale={{
                        emptyText: t('No active share links')
                    }}
                />
            </div>

            {/* Instructions */}
            <div style={{ marginTop: 24, padding: 16, background: '#f6f8fa', borderRadius: 6 }}>
                <Title level={5}>{t('How it works:')}</Title>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                    <li>{t('Share links allow anyone to access and submit your form')}</li>
                    <li>{t('No login required for form submitters')}</li>
                    <li>{t('Links automatically expire based on your settings')}</li>
                    <li>{t('You can revoke access at any time')}</li>
                    <li>{t('Track submissions in real-time')}</li>
                </ul>
            </div>

            <Modal open={showRecipientsModal} title={t('Add Recipients')} onCancel={handleCloseRecipientModal} onOk={handleCloseRecipientModal}>
                <div className=''>
                    <div className='mb-2'>{t('Recipients')}</div>
                    <Input placeholder={t('eg: test1@gmail.com, test2@gmail.com')} value={recipients} onChange={(e) => setRecipients(e.target.value)} />
                    {errorMessage ? <div className='text-red-400'>{errorMessage}</div> : null}
                    <Flex className='mt-4' justify="flex-end" gap={16}>
                        <Button className='submit-button' onClick={handleShareLink}>{t('Share')}</Button>
                        <Button onClick={handleCloseRecipientModal}>{t('Cancel')}</Button>
                    </Flex>
                </div>

            </Modal>
        </Modal>
    );
};

export default ShareFormModal; 
